import { styled } from '@mui/material'
import { useState } from 'react'
import { useIsDarkMode } from '@omnea-app/omui/useIsDarkMode'

type OrgLogoProps = {
  name: string
  src?: string
  darkSrc?: string
}

// Logos are uploaded at any shape, so the design fixes their height and caps their width: a square mark
// renders 18×18, a long wordmark stops at 96 wide and shrinks to fit rather than stretching the toggle.
const Image = styled('img')({
  display: 'block',
  height: '1.125rem',
  width: 'auto',
  maxWidth: '6rem',
  objectFit: 'contain',
})

const Name = styled('span')(({ theme }) => ({
  ...theme.typography['body-medium'],
  color: theme.palette.text.default,
  maxWidth: '10rem',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}))

const LoadedLogo = ({ name, src }: { name: string; src: string }) => {
  const [failed, setFailed] = useState(false)
  return failed ? <Name>{name}</Name> : <Image src={src} alt="" onError={() => setFailed(true)} />
}

/** The organisation's wordmark, or its name when there is no logo to show. */
export const OrgLogo = ({ name, src, darkSrc }: OrgLogoProps) => {
  const isDark = useIsDarkMode()
  // A light-background wordmark is often dark ink, unreadable on the dark surface, so dark mode without
  // its own logo shows the name instead.
  const logo = isDark ? darkSrc : src

  return logo == null ? <Name>{name}</Name> : <LoadedLogo key={logo} name={name} src={logo} />
}
